<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BulkIndexContractServiceFee',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    items: {
      type: Array,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const labelAlign = ref('right');
  const formId = ref(null);
  const formName = ref('BulkIndexContractServiceFee');
  const item = ref({
    percentage: null,
    new_indexation_date: null,
    valid_from: null,
    transaction_notes: null,
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    formId: formId.value,
    formName: formName.value,
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      { name: 'formId' },
      { name: 'formName' },
      {
        name: 'item',
        cache: { type: 'local', key: 'BulkIndexContractServiceFee.item' },
      },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId },
    ux: {},
    dataRefs: {
      labelAlign,
      formId,
      formName,
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'BulkIndexContractServiceFee|Title',
        'Indexing'
      ),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.items,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('items');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_o6gt32fd5_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_mkosb8t5a_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_1040167445267876_request_0x98u6wmj_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'bulk_index_contract_service_fee',
      data: {
        items: _.map(props.items, (p) =>
          _.extend(_.pick(p, ['id', 'etag']), { value: item.value })
        ),
      },
    };
  };

  const act_7315092382398562_closeModal_o6gt32fd5 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_o6gt32fd5_getActionArgs,
    });
  const inst_evh_7315092382398562_close_0qu0y35ly =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_o6gt32fd5,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_0qu0y35ly],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_qxdb84ts9 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_f2xfjsqp4 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_qxdb84ts9,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_f2xfjsqp4],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_7e5l6l7hn =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_59bcprz3p =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_7e5l6l7hn,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_59bcprz3p],
    event: 'close',
  });
  const act_1040167445267876_closeModal_mkosb8t5a =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_mkosb8t5a_getActionArgs,
    });
  const inst_evh_1040167445267876_success_b6jvaitpt =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_mkosb8t5a,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_0x98u6wmj = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_0x98u6wmj_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_1040167445267876_success_b6jvaitpt],
  });
  const inst_evh_1040167445267876_submit_2r2yu06sx =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_0x98u6wmj,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_2r2yu06sx],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_a9pph0iko =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_nqldn1p9v =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_a9pph0iko,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_nqldn1p9v],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_0qu0y35ly,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_f2xfjsqp4,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_59bcprz3p,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_b6jvaitpt,
    inst_evh_1040167445267876_submit_2r2yu06sx,
    submit: inst_evh_1040167445267876_submit_2r2yu06sx,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_nqldn1p9v,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_0qu0y35ly = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_0qu0y35ly.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_f2xfjsqp4 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_f2xfjsqp4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_59bcprz3p = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_59bcprz3p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_b6jvaitpt = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_b6jvaitpt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_2r2yu06sx = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_2r2yu06sx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_nqldn1p9v = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_nqldn1p9v.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="BulkIndexContractServiceFee"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :resourceTitle="
          pgettext(
            'BulkIndexContractServiceFee|ResourceTitle',
            'Contract Service Fees'
          )
        "
        :resourceKey="
          pgettext('BulkIndexContractServiceFee|ResourceKey', 'Indexing')
        "
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        autofocus="first"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="percentage"
          :label="pgettext('BulkIndexContractServiceFee', 'Percentage')"
          :description="
            pgettext(
              'BulkIndexContractServiceFee',
              'The percentage used to calculate the indexed prices.'
            )
          "
          :label-cols-md="3"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
          appendText="%"
          required
        >
          <ItpFormInput
            name="percentage"
            v-model="item.percentage"
            :value="item.percentage"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.percentage =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            mask="number"
            unmask="typed"
            :scale="4"
            required
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="valid_from"
          :label="pgettext('BulkIndexContractServiceFee', 'Valid From')"
          :description="
            pgettext(
              'BulkIndexContractServiceFee',
              'The start date from which the new fee is valid.'
            )
          "
          :label-cols-md="3"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormDatetime name="valid_from" v-model="item.valid_from" required>
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="new_indexation_date"
          :label="
            pgettext('BulkIndexContractServiceFee', 'New Indexation Date')
          "
          :description="
            pgettext(
              'BulkIndexContractServiceFee',
              'Sets the next indexation date for the related contracts.'
            )
          "
          :label-cols-md="3"
          :content-cols-md="4"
          :labelAlign="labelAlign"
        >
          <ItpFormDatetime
            name="new_indexation_date"
            v-model="item.new_indexation_date"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="transaction_notes"
          :label="pgettext('ContractServiceFee', 'Transaction notes')"
          :label-cols-md="3"
          :content-cols-md="9"
          :labelAlign="labelAlign"
        >
          <TransactionNotesEditor
            type="number"
            name="transaction_notes"
            v-model="item.transaction_notes"
            :value="item.transaction_notes"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.transaction_notes =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            class="w-auto"
          >
          </TransactionNotesEditor>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Submit')"
          :disabled="!forms[formName] || !forms[formName].submittable"
          type="submit"
          :form="formId"
          variant="primary"
          :icon="$config.submitIcon"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
